import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  CloseOutlined,
  EyeOutlined,
  FilterOutlined,
  HomeOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useTranslation } from "react-i18next";

import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { AppContext } from "../../../context/AppContextProvider";
import {
  getBranches,
  getZatcaCrProfiles,
  getZatcaInvoiceIssues,
  reportBulkZatcaInvoices,
} from "../../../network/network";
import {
  UserType,
  ZatcaInvoiceStatus,
  ZatcaInvoiceSubtype,
  zatcaInvoiceStatuses,
  zatcaInvoiceSubtypes,
  zatcaResponseStatuses,
} from "../../../configs/constants";
import InvoiceDetailDrawer from "./InvoiceDetailDrawer";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const getOption = (options, id) =>
  options.find(
    (item) => item.id === id || item.value === id || item.name === id,
  );

const getZatcaValue = (value) => {
  if (!value) return null;

  if (typeof value === "object") {
    return value.value || value.id || value.name || value.key;
  }

  return value;
};

const getOptionTag = (options, value, t) => {
  const id = getZatcaValue(value);
  const option = getOption(options, id);

  if (option) {
    return <Tag color={option.color}>{t(option.text)}</Tag>;
  }

  if (value?.label) {
    return <Tag>{value.label}</Tag>;
  }

  return id ? <Tag>{t(id)}</Tag> : null;
};

const getCrProfilesFromResponse = (response) => {
  const data = response?.data?.data || response?.data || {};

  return data?.profiles || data?.zatca_cr_profiles || data?.cr_profiles || [];
};

const formatDateTime = (date) => {
  if (!date) return "-";

  const parsedDate = moment(date);

  return parsedDate.isValid() ? parsedDate.format("MMM. D, YYYY hh:mm a") : "-";
};

const getCrNumber = (invoice) => {
  return (
    invoice?.cr ||
    invoice?.cr_number ||
    invoice?.zatca_cr_profile?.cr ||
    invoice?.zatca_cr_profile?.cr_number ||
    invoice?.branch?.cr ||
    "-"
  );
};

const getBranchName = (invoice) => {
  return (
    invoice?.branch?.name || invoice?.branch_name || invoice?.branch_id || "-"
  );
};

const isBulkReportableB2CInvoice = (invoice) => {
  const status = getZatcaValue(invoice?.status_details || invoice?.status);
  const subtype = getZatcaValue(
    invoice?.invoice_subtype_details || invoice?.invoice_subtype,
  );

  return (
    invoice?.id &&
    subtype === ZatcaInvoiceSubtype.Simplified &&
    (status === ZatcaInvoiceStatus.Pending ||
      status === ZatcaInvoiceStatus.Failed)
  );
};

const IssueCards = ({ issues, type, t }) => {
  const isError = type === "error";
  const safeIssues = Array.isArray(issues) ? issues : [];

  if (!safeIssues.length) {
    return <Text type="secondary">-</Text>;
  }

  return (
    <Space direction="vertical" size={6} className="app-w-full">
      {safeIssues.slice(0, 2).map((issue, index) => (
        <Alert
          key={`${type}-${issue?.code || index}`}
          type={isError ? "error" : "warning"}
          showIcon
          message={
            issue?.code ||
            issue?.status ||
            (isError ? t("error") : t("warning"))
          }
          description={issue?.message || "-"}
        />
      ))}

      {safeIssues.length > 2 && (
        <Tag color={isError ? "red" : "gold"}>+{safeIssues.length - 2}</Tag>
      )}
    </Space>
  );
};

const ZatcaInvoiceIssues = () => {
  const { authUser, appConfig } = useContext(AppContext);
  const { t } = useTranslation();
  const [searchFormRef] = Form.useForm();

  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [issuesTableData, setIssuesTableData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [crProfilesData, setCrProfilesData] = useState([]);
  const [searchParams, setSearchParams] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(15);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [bulkReportLoading, setBulkReportLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [modal, contextHolder] = Modal.useModal();
  const [paginationData, setPaginationData] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDrawerOpen, setInvoiceDrawerOpen] = useState(false);

  const isOwner = authUser?.type === UserType.BusinessOwner;

  useEffect(() => {
    setIsDataLoading(true);

    getZatcaInvoiceIssues({
      ...searchParams,
      page: currentPage,
      per_page: perPageSize,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          setIssuesTableData([]);
          setPaginationData({
            current_page: 1,
            per_page: perPageSize,
            total: 0,
          });
          return;
        }

        setIssuesTableData(response?.data?.data?.zatca_invoices || []);
        setPaginationData(
          response?.data?.data?.pagination || {
            current_page: 1,
            per_page: perPageSize,
            total: 0,
          },
        );
      })
      .catch((error) => {
        setIssuesTableData([]);
        setPaginationData({
          current_page: 1,
          per_page: perPageSize,
          total: 0,
        });

        handleApiError(error, t);
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, currentPage, perPageSize, reloadTableData, t]);

  useEffect(() => {
    if (!isOwner) return;

    setIsOptionsLoading(true);

    Promise.all([
      getBranches({ per_page: -1 }),
      getZatcaCrProfiles({ per_page: -1 }),
    ])
      .then(([branchesResponse, crProfilesResponse]) => {
        if (branchesResponse?.data?.success === false) {
          handleApiError(branchesResponse, t);
          setBranchesData([]);
        } else {
          setBranchesData(branchesResponse.data.data.branches);
        }

        if (crProfilesResponse?.data?.success === false) {
          handleApiError(crProfilesResponse, t);
          setCrProfilesData([]);
        } else {
          setCrProfilesData(getCrProfilesFromResponse(crProfilesResponse));
        }
      })
      .catch((error) => {
        handleApiError(error, t);
        setBranchesData([]);
        setCrProfilesData([]);
      })
      .finally(() => {
        setIsOptionsLoading(false);
      });
  }, [isOwner, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "zatca-invoice-issues",
      breadcrumbName: t("zatca_invoice_issues"),
      icon: <WarningOutlined />,
    },
  ];

  const openInvoiceDrawer = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceDrawerOpen(true);
  };

  const handleSearch = (values) => {
    setDrawerVisible(false);
    let search_params = false;

    if (values.dates) {
      let startDateUtc = moment.utc(values.dates[0]).set({
        date: values.dates[0].date() - 1,
        month: values.dates[0].month(),
        year: values.dates[0].year(),
        hour: 19,
        minute: 0,
        second: 0,
        millisecond: 0,
      });

      let endDateUtc = moment.utc(values.dates[1]).set({
        date: values.dates[1].date(),
        month: values.dates[1].month(),
        year: values.dates[1].year(),
        hour: 18,
        minute: 59,
        second: 59,
        millisecond: 0,
      });

      search_params = {
        search_start_date: startDateUtc.format(),
        search_end_date: endDateUtc.format(),
      };
    }

    if (values.type) {
      search_params = {
        type: values.type,
        ...search_params,
      };
    }

    if (values.subtype) {
      search_params = {
        subtype: values.subtype,
        ...search_params,
      };
    }

    if (values.search) {
      search_params = {
        search: values.search.trim(),
        ...search_params,
      };
    }

    if (isOwner && values.branch_id) {
      search_params = {
        branch_id: values.branch_id,
        ...search_params,
      };
    }

    if (isOwner && values.zatca_cr_profile_id) {
      search_params = {
        zatca_cr_profile_id: values.zatca_cr_profile_id,
        ...search_params,
      };
    }

    setCurrentPage(1);
    setSelectedRowKeys([]);
    setSelectedInvoiceIds([]);

    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams({});
    }
  };

  const handleReset = () => {
    setDrawerVisible(false);
    searchFormRef.resetFields();
    setCurrentPage(1);
    setSelectedRowKeys([]);
    setSelectedInvoiceIds([]);
    setSearchParams({});
  };

  const handleBulkReportB2C = () => {
    if (selectedInvoiceIds.length > 500) {
      showAppError(t("bulk_report_b2c_max_limit"));
      return;
    }

    modal.confirm({
      title: t("bulk_report_b2c_invoices"),
      content: selectedInvoiceIds.length
        ? t("bulk_report_selected_b2c_confirm", {
            count: selectedInvoiceIds.length,
          })
        : t("bulk_report_all_b2c_confirm"),
      okText: t("ok"),
      cancelText: t("cancel"),
      centered: true,
      onOk: () => {
        setBulkReportLoading(true);

        const payload = selectedInvoiceIds.length
          ? { invoice_ids: selectedInvoiceIds }
          : {};

        return reportBulkZatcaInvoices(payload)
          .then((response) => {
            if (response?.data?.success === false) {
              handleApiError(response, t);
              return;
            }

            NotificationWithIcon(
              "success",
              t("bulk_report_b2c_success"),
              response?.data?.message ||
                t("bulk_report_b2c_dispatched_count", {
                  count: response?.data?.data?.dispatched_count || 0,
                }),
            );

            setSelectedRowKeys([]);
            setSelectedInvoiceIds([]);
            setReloadTableData(!reloadTableData);
          })
          .catch((error) => {
            handleApiError(error, t);
          })
          .finally(() => {
            setBulkReportLoading(false);
          });
      },
    });
  };

  const handleTableChange = (pagination) => {
    const nextPageSize = Math.min(pagination.pageSize || 15, 200);

    setCurrentPage(pagination.current || 1);
    setPerPageSize(nextPageSize);
    setPaginationData((prev) => ({
      ...prev,
      current_page: pagination.current || 1,
      per_page: nextPageSize,
    }));
    setSelectedRowKeys([]);
    setSelectedInvoiceIds([]);
  };

  const getBranchLabel = (branchId) => {
    const branch = branchesData.find((item) => item.id === branchId);

    return branch?.name || branchId;
  };

  const getCrProfileLabel = (profileId) => {
    const profile = crProfilesData.find((item) => item.id === profileId);

    if (!profile) return profileId;

    return profile.business_arabic_name
      ? `${profile.cr} - ${profile.business_arabic_name}`
      : profile.cr;
  };

  const columns = [
    {
      title: t("uuid"),
      dataIndex: "uuid",
      render: (uuid) => <Text copyable={!!uuid}>{uuid || "-"}</Text>,
    },
    ...(isOwner
      ? [
          {
            title: t("branch"),
            render: (record) => getBranchName(record),
          },
          {
            title: t("cr_number"),
            render: (record) => getCrNumber(record),
          },
        ]
      : []),
    {
      title: t("status"),
      render: (record) => (
        <Space direction="vertical" size={4}>
          {getOptionTag(
            zatcaInvoiceStatuses,
            record?.status_details || record?.status,
            t,
          )}

          {getOptionTag(zatcaInvoiceSubtypes, record?.invoice_subtype, t)}

          {record?.processing_mode ? (
            <Tag
              color={
                record.processing_mode === "clearance" ? "default" : "blue"
              }
            >
              {t(record.processing_mode)}
            </Tag>
          ) : null}

          {getOptionTag(zatcaResponseStatuses, record?.response_status, t)}
        </Space>
      ),
    },
    {
      title: t("warnings"),
      render: (record) => (
        <IssueCards issues={record?.warnings} type="warning" t={t} />
      ),
    },
    {
      title: t("zatca_errors"),
      render: (record) => (
        <IssueCards issues={record?.errors} type="error" t={t} />
      ),
    },
    {
      title: t("submitted_at"),
      render: (record) =>
        formatDateTime(record?.submitted_at || record?.created_at),
    },
    {
      title: t("action"),
      width: 85,
      render: (record) => (
        <Button type="link" onClick={() => openInvoiceDrawer(record)}>
          <EyeOutlined title={t("view_invoice")} />
        </Button>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("zatca_invoice_issues")}
          ghost={false}
          extra={[
            isOwner && (
              <Button
                type="primary"
                loading={bulkReportLoading}
                onClick={handleBulkReportB2C}
                key="bulk-report-b2c"
              >
                {selectedInvoiceIds.length
                  ? `${t("bulk_report_b2c_invoices")} (${selectedInvoiceIds.length})`
                  : t("bulk_report_b2c_invoices")}
              </Button>
            ),

            <Button
              type="primary"
              onClick={() => {
                setDrawerVisible(true);
              }}
              key="filter"
            >
              <FilterOutlined />
            </Button>,
          ]}
        />
      </Card>

      <Card>
        {Object.keys(searchParams).length !== 0 && (
          <div style={{ marginBottom: "15px" }}>
            {searchParams.type && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.type;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["type"]);
                }}
              >
                {t("issue_type")}:{" "}
                {searchParams.type === "warnings"
                  ? t("warnings")
                  : searchParams.type === "errors"
                    ? t("zatca_errors")
                    : ""}
              </Tag>
            )}

            {searchParams.subtype && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.subtype;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["subtype"]);
                }}
              >
                {t("subtype")}: {t(`zatca_subtype_${searchParams.subtype}`)}
              </Tag>
            )}

            {searchParams.search && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.search;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["search"]);
                }}
              >
                {t("search")}: {searchParams.search}
              </Tag>
            )}

            {searchParams.branch_id && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.branch_id;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["branch_id"]);
                }}
              >
                {t("branch")}: {getBranchLabel(searchParams.branch_id)}
              </Tag>
            )}

            {searchParams.zatca_cr_profile_id && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.zatca_cr_profile_id;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["zatca_cr_profile_id"]);
                }}
              >
                {t("zatca_cr_profile")}:{" "}
                {getCrProfileLabel(searchParams.zatca_cr_profile_id)}
              </Tag>
            )}

            {searchParams.search_start_date && searchParams.search_end_date && (
              <Tag
                color="blue"
                closeIcon
                onClose={() => {
                  delete searchParams.search_start_date;
                  delete searchParams.search_end_date;
                  setReloadTableData(!reloadTableData);
                  searchFormRef.resetFields(["dates"]);
                }}
              >
                {t("date_range")}:{" "}
                {moment(searchParams.search_start_date).format("MMM. D, YYYY")}{" "}
                - {moment(searchParams.search_end_date).format("MMM. D, YYYY")}
              </Tag>
            )}
          </div>
        )}

        <Table
          columns={columns}
          dataSource={issuesTableData}
          loading={isDataLoading}
          rowKey={(record) => record?.id || record?.uuid}
          rowSelection={
            isOwner
              ? {
                  selectedRowKeys,
                  preserveSelectedRowKeys: true,
                  onChange: (keys, rows) => {
                    const ids = rows
                      .filter((row) => isBulkReportableB2CInvoice(row))
                      .map((row) => row.id)
                      .filter(Boolean);

                    setSelectedRowKeys(keys);
                    setSelectedInvoiceIds(ids);
                  },
                  getCheckboxProps: (record) => ({
                    disabled: !isBulkReportableB2CInvoice(record),
                  }),
                }
              : undefined
          }
          scroll={{ x: 1400 }}
          pagination={{
            current: paginationData.current_page,
            pageSize: paginationData.per_page,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Drawer
        title={t("filters")}
        placement={appConfig.direction === "rtl" ? "left" : "right"}
        onClose={() => {
          setDrawerVisible(false);
        }}
        open={drawerVisible}
        closable={false}
        extra={
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              setDrawerVisible(false);
            }}
          />
        }
      >
        <Form
          name="search-zatca-invoice-issues-form"
          layout="vertical"
          onFinish={handleSearch}
          form={searchFormRef}
        >
          <Form.Item name="type" label={t("issue_type")}>
            <Select
              allowClear
              options={[
                {
                  label: t("warnings"),
                  value: "warnings",
                },
                {
                  label: t("zatca_errors"),
                  value: "errors",
                },
              ]}
            ></Select>
          </Form.Item>

          <Form.Item name="subtype" label={t("subtype")}>
            <Select
              allowClear
              options={[
                {
                  label: t("zatca_subtype_standard"),
                  value: ZatcaInvoiceSubtype.Standard,
                },
                {
                  label: t("zatca_subtype_simplified"),
                  value: ZatcaInvoiceSubtype.Simplified,
                },
              ]}
            ></Select>
          </Form.Item>

          <Form.Item name="search" label={t("search")}>
            <Input
              allowClear
              placeholder={t("search_invoice_issues_placeholder")}
            />
          </Form.Item>

          <Form.Item name="dates" label={t("date_range")}>
            <RangePicker
              allowClear
              format="DD/MM/YYYY"
              className="app-w-full"
            />
          </Form.Item>

          {isOwner && (
            <>
              <Form.Item name="branch_id" label={t("branch")}>
                <Select
                  allowClear
                  showSearch
                  loading={isOptionsLoading}
                  optionFilterProp="label"
                  options={branchesData.map((branch) => {
                    return {
                      label: branch.name,
                      value: branch.id,
                    };
                  })}
                ></Select>
              </Form.Item>

              <Form.Item
                name="zatca_cr_profile_id"
                label={t("zatca_cr_profile")}
              >
                <Select
                  allowClear
                  showSearch
                  loading={isOptionsLoading}
                  optionFilterProp="label"
                  options={crProfilesData.map((profile) => {
                    return {
                      label: profile.business_arabic_name
                        ? `${profile.cr} - ${profile.business_arabic_name}`
                        : profile.cr,
                      value: profile.id,
                    };
                  })}
                ></Select>
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Input
              type="submit"
              value={t("search")}
              className="app-search-submit"
            />
          </Form.Item>

          <Form.Item>
            <Input
              type="button"
              value={t("reset")}
              onClick={handleReset}
              style={{
                width: "80px",
              }}
            />
          </Form.Item>
        </Form>
      </Drawer>

      <InvoiceDetailDrawer
        open={invoiceDrawerOpen}
        transactionId={
          selectedInvoice?.transaction_id || selectedInvoice?.transaction?.id
        }
        invoiceData={selectedInvoice}
        onClose={() => {
          setInvoiceDrawerOpen(false);
          setSelectedInvoice(null);
        }}
        onRetried={() => setReloadTableData(!reloadTableData)}
      />
    </>
  );
};

export default ZatcaInvoiceIssues;
